'use client'

import { motion } from 'framer-motion'
import { Card, CardContent } from '@/components/ui/card'
import { CardWatermark } from '@/components/ui/card-watermark'
import { Icons } from '@/components/ui/icons'
import { cn } from '@/lib/utils'

interface FatalCountCardProps {
  count: number
  trend?: { value: string; positive: boolean }
  onClick?: () => void
  isLoading?: boolean
}

export function FatalCountCard({ count, trend, onClick, isLoading = false }: FatalCountCardProps) {
  if (isLoading) {
    return (
      <Card className='group relative h-full overflow-hidden'>
        <CardWatermark opacity={3} scale={0.9} />
        <CardContent className='relative z-10 p-5'>
          <div className='flex h-[120px] items-center justify-center'>
            <Icons.loader className='h-6 w-6 animate-spin text-brand-cornflower' />
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      onClick={onClick}
      className={onClick ? 'cursor-pointer' : ''}
    >
      <Card className='group relative h-full overflow-hidden border-red-100'>
        <CardWatermark opacity={3} scale={0.9} />
        <CardContent className='relative z-10 p-5'>
          <div className='flex items-start justify-between'>
            {/* Left side - Info */}
            <div className='space-y-2'>
              <p className='text-micro uppercase text-brand-muted transition-colors duration-200 group-hover:text-red-500'>
                Fatal Count
              </p>
              <motion.p
                className='font-display text-[2.25rem] font-bold leading-none tracking-tight text-red-600'
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              >
                {count}
              </motion.p>
              {trend && (
                <motion.p
                  className={cn(
                    'flex items-center gap-1 text-xs font-medium',
                    trend.positive ? 'text-emerald-600' : 'text-red-500'
                  )}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  {trend.positive ? (
                    <Icons.trendingUp className='h-3 w-3 rotate-180' strokeWidth={2} />
                  ) : (
                    <Icons.trendingUp className='h-3 w-3' strokeWidth={2} />
                  )}
                  {trend.value}
                </motion.p>
              )}
            </div>

            {/* Right side - Icon */}
            <motion.div
              className='rounded-xl bg-gradient-to-br from-red-500 to-red-600 p-2.5 text-white shadow-lg'
              whileHover={{ scale: 1.15, rotate: 5 }}
              transition={{ type: 'spring', stiffness: 400, damping: 17 }}
            >
              <Icons.alertCircle className='h-5 w-5' strokeWidth={1.5} />
            </motion.div>
          </div>

          {/* Description */}
          <div className='mt-3 rounded-lg bg-red-50 p-2'>
            <p className='text-xs text-red-700'>
              Critical policy violations detected across all calls
            </p>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
}




